import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { DialogRef } from '@components/dialog/dialog-ref';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DeliveryOrder } from '@core/models/delivery/delivery-order';
import { Subject, takeUntil } from 'rxjs';
import { ToastService } from '@core/state/toast/toast.service';
import { DeliveriesService } from '../deliveries.service';
import { TranslateService } from '@ngx-translate/core';
import { DeliveryOrderRefund } from '@core/models/delivery/refund/delivery-order-refund';
import { ApiErrorResponse } from '@core/data/api/error-response';
import { DropdownInputType } from '@components/dropdown/dropdown-input.type';


@Component({
    selector: 'ihd-dialog-delivery-order-refund',
    templateUrl: 'dialog-delivery-order-refund.component.html',
    styleUrls: ['./dialog-delivery-order-refund.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'c-dialog-delivery-order-refund',
    },
})
export class DialogDeliveryOrderRefundComponent implements OnInit, OnDestroy {
    public config;
    public order: DeliveryOrder;
    public reasonOther: string = 'Order.Refund.Other';
    public refundReasons: DropdownInputType[];
    public editForm: FormGroup;
    public submitted: boolean = false;
    public busy: boolean = false;

    private destroyed$: Subject<void> = new Subject();

    constructor(
        public dialogRef: DialogRef<DialogDeliveryOrderRefundComponent>,
        private toastService: ToastService,
        private ordersService: DeliveriesService,
        private translateService: TranslateService,
        private cdr: ChangeDetectorRef
    ) {
        if (dialogRef.containerInstance && dialogRef.containerInstance.config) {
            this.config = dialogRef.containerInstance.config;
            this.order = this.config.order;
            this.refundReasons = [
                {
                    id: 'Order.Refund.NoDriver',
                    label: 'Order.Refund.NoDriver',
                },
                {
                    id: 'Order.Refund.NoDeliver',
                    label: 'Order.Refund.NoDeliver',
                },
                {
                    id: 'Order.Refund.Damaged',
                    label: 'Order.Refund.Damaged',
                },
                {
                    id: this.reasonOther,
                    label: this.reasonOther,
                },
            ];

            this.createForm();
        }
    }

    ngOnInit() {}

    ngOnDestroy() {
        this.destroyed$.next();
    }

    sendRequest() {
        this.submitted = true;
        this.cdr.markForCheck();

        if (this.editForm.valid) {
            this.busy = true;
            this.cdr.markForCheck();

            const { reason, noteOther } = this.editForm.value;
            const request = new DeliveryOrderRefund();
            let refundValue: string;

            if (reason.id === this.reasonOther) {
                refundValue = noteOther;
            } else {
                refundValue = this.translateService.instant(reason.id);
            }

            request.refundNote = refundValue;

            this.ordersService.requestRefund(this.order, request).subscribe(
                (res: any) => {
                    if (res.payload.success) {
                        this.close();
                    } else {
                        // user hit cancel
                        this.busy = false;
                        this.cdr.markForCheck();
                    }
                },
                (error: { payload: ApiErrorResponse }) => {
                    this.toastService.showApiError(error);
                    this.busy = false;
                    this.cdr.markForCheck();
                }
            );
        } else {
            this.toastService.showWarning('Errors.FormInvalid');
        }
    }

    close() {
        this.dialogRef.close();
    }

    private createForm() {
        this.editForm = new FormGroup({
            reason: new FormControl(null, Validators.required),
            noteOther: new FormControl(null),
        });

        this.editForm
            .get('reason')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe((value) => {
                const noteControl = this.editForm.get('noteOther');
                if (value && value.id === this.reasonOther) {
                    noteControl.setValidators(Validators.required);
                } else {
                    noteControl.clearValidators();
                }

                noteControl.updateValueAndValidity();
            });
    }
}
